import React, { useState, useContext, useRef } from "react";
import styled from "styled-components";
import { FaUserCircle, FaCamera, FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { useGetUserInfoQuery, useUpdateUserMutation } from "../store/apis/userApi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import BackgroundImage from "../components/BackgroundImage";
import background from "../assets/editProfile.webp";
import ProfilePictureSettings from "../utils/profilePictureSettings";

// ✅ Toast notifications (modern replacement for alert)
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditProfile() {
  const navigate = useNavigate();
  const settings = useContext(ProfilePictureSettings);
  const { data: userProfile } = useGetUserInfoQuery();
  const [updateProfile, { isLoading }] = useUpdateUserMutation();

  const [formData, setFormData] = useState({
    name: userProfile?.user?.name || "",
    email: userProfile?.user?.email || "",
    organisation: userProfile?.user?.organisation || "",
    password: "",
    confirmPassword: "",
    profilePicture: null,
  });

  const [previewImage, setPreviewImage] = useState(userProfile?.user?.photo || null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef();

  // ✅ Input change handler
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Profile picture handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData({ ...formData, profilePicture: file });

    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  // ✅ Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("organisation", formData.organisation);
    if (formData.password) formDataToSend.append("password", formData.password);
    if (formData.profilePicture) formDataToSend.append("profilePicture", formData.profilePicture);

    try {
      const response = await updateProfile(formDataToSend);
      if (response?.data?.status === "success") {
        toast.success("Profile updated successfully!");
        navigate("/profile");
      } else {
        throw new Error("Update failed.");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Could not update profile.");
    }
  };

  return (
    <Container>
       <Helmet>
        <title>Edit Profile | SolarAIDD</title>
        <meta name="description" content="Edit your personal information and update your password." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://solaraidd.csir.co.za/editProfile" />
      </Helmet>

      <BackgroundImage background={background} />

      <div className="container">
        {/* Back Button */}
        <button className="back-button" onClick={() => navigate(-1)}>
          <FaArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="title">
          <h1>Edit Profile</h1>
          <p>Keep your information up to date.</p>
        </div>

        <form className="form-container" onSubmit={handleSubmit}>
          <div className="form">
            {/* Profile Picture */}
            <div className="img-info" onClick={() => fileInputRef.current.click()}>
              {previewImage ? (
                <img src={previewImage} alt="Profile Preview" />
              ) : (
                <FaUserCircle size={80} color="#FFFFFF" />
              )}
              <div className="edit-overlay">
                <FaCamera size={20} />
              </div>
              <input type="file" ref={fileInputRef} onChange={handleFileChange} hidden accept="image/*" />
            </div>

            {/* Inputs */}
            <label>
              Name
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
            </label>

            <label>
              Email
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </label>

            <label>
              Organization
              <input type="text" name="organisation" value={formData.organisation} onChange={handleInputChange} />
            </label>

            <label>
              Password (optional)
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <span onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </label>

            <label>
              Confirm Password
              <div className="password-field">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                />
                <span onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </label>

            {/* Submit Button */}
            <button type="submit" disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Profile"}
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
}

const Container = styled.div`
  position: relative;

  .container {
    position: absolute;
    top: 4rem;
    left: 0;
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;

    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: none;
      color: white;
      font-size: 1rem;
      cursor: pointer;
      position: absolute;
      top: 1rem;
      left: 1rem;
      transition: color 0.3s;
    }
    .back-button:hover {
      color: #e55b41;
    }

    .title {
      text-align: center;
      color: white;
      h1 {
        font-size: 2rem;
        font-weight: 700;
      }
      p {
        font-size: 1rem;
        color: #ddd;
      }
    }

    .form-container {
      padding: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;

      .form {
        padding: 2rem;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(12px);
        width: 90%;
        max-width: 450px;
        color: white;
        border-radius: 20px;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.65);
        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        .img-info {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;

          img {
            height: 6rem;
            width: 6rem;
            border-radius: 50%;
            object-fit: cover;
          }

          .edit-overlay {
            position: absolute;
            bottom: 0;
            right: 35%;
            background: rgba(0,0,0,0.6);
            padding: 0.5rem;
            border-radius: 50%;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: 0.3s;
          }

          &:hover .edit-overlay {
            background: #e55b41;
          }
        }

        label {
          display: flex;
          flex-direction: column;
          font-size: 1rem;
          gap: 0.5rem;

          input {
            padding: 0.75rem 1rem;
            width: 100%;
            border: 1px solid #ccc;
            border-radius: 8px;
            outline: none;
            transition: 0.3s;
          }
          input:focus {
            border-color: #e55b41;
            box-shadow: 0 0 5px #e55b41;
          }
        }

        .password-field {
          display: flex;
          align-items: center;
          background: #fff;
          border-radius: 8px;
          overflow: hidden;

          input {
            border: none;
            flex: 1;
            padding: 0.75rem;
          }
          span {
            padding: 0.75rem;
            cursor: pointer;
            color: #333;
          }
        }

        button {
          padding: 0.75rem;
          background-color: #e55b41;
          border: none;
          cursor: pointer;
          color: white;
          border-radius: 8px;
          font-weight: bold;
          font-size: 1rem;
          transition: all 0.3s ease-in-out;
        }

        button:disabled {
          background-color: #888;
          cursor: not-allowed;
        }

        button:hover:not(:disabled) {
          background-color: #d14f38;
          transform: translateY(-2px);
        }
      }
    }
  }
`;